import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Check, Sparkles } from "lucide-react";
import { api } from "../../services/api";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import SiteFooter from "../common/SiteFooter";
import { isAstriaDomain } from "../../utils/branding";

type Plan = {
  id: string;
  planName: string;
  price: string;
  cadence: string;
  highlight: string;
  features: string[];
  popular: boolean;
  active: boolean;
};

// HealJai (Thailand) offers only these plans, shown in this order. Any other
// plan returned by the backend is hidden from this page.
const VISIBLE_PLAN_NAMES = ["Free Trial", "Thailand Annual", "Thailand Monthly"];

const planNameKey = (name: string): string => name.trim().toLowerCase();

const normalizePlan = (raw: any): Plan => {
  const features = Array.isArray(raw?.features)
    ? raw.features
    : typeof raw?.features === "string"
      ? raw.features.split(/[,\n]/g)
      : [];

  return {
    id: raw?.id || raw?._id || "",
    planName: raw?.planName || "",
    price: raw?.price || "",
    cadence: raw?.cadence || raw?.billingCadence || "ต่อเดือน",
    highlight: raw?.highlight || "",
    features: features.map((item: string) => item.trim()).filter(Boolean),
    popular: Boolean(raw?.popular ?? raw?.isPopular ?? false),
    active: Boolean(raw?.active ?? raw?.isActive ?? true),
  };
};

const Subscription: React.FC = () => {
  if (isAstriaDomain()) {
    return <Navigate to="/journey" replace />;
  }

  return <SubscriptionContent />;
};

const SubscriptionContent: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [checkoutPlanId, setCheckoutPlanId] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState("");

  const fetchPlans = async () => {
    setIsLoading(true);
    try {
      const res = await api.get("/backend/subscriptionPlans");
      const payload =
        res?.data?.plans ||
        res?.data?.subscriptionPlans ||
        res?.data?.data ||
        res?.data ||
        [];
      const list = Array.isArray(payload) ? payload : [];
      setPlans(list.map(normalizePlan));
    } catch (err) {
      setPlans([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const activePlans = useMemo(() => {
    const rank = (plan: Plan) =>
      VISIBLE_PLAN_NAMES.map(planNameKey).indexOf(planNameKey(plan.planName));
    return plans
      .filter((plan) => plan.active && rank(plan) !== -1)
      .sort((a, b) => rank(a) - rank(b));
  }, [plans]);

  const currentPlanId = useMemo(
    () => user?.subscriptionId || localStorage.getItem("subscriptionId") || "",
    [user],
  );
  const subscriptionStatus = useMemo(
    () =>
      user?.subscriptionStatus ||
      localStorage.getItem("subscriptionStatus") ||
      "",
    [user],
  );
  const subscriptionEndDate = useMemo(
    () =>
      user?.subscriptionEndDate ||
      localStorage.getItem("subscriptionEndDate") ||
      "",
    [user],
  );
  const isPlanExpired = useMemo(() => {
    if (!subscriptionEndDate) return false;
    const end = new Date(subscriptionEndDate);
    if (Number.isNaN(end.getTime())) return false;
    return end.getTime() < Date.now();
  }, [subscriptionEndDate]);
  const isSubscriptionExpired =
    subscriptionStatus === "expired" || isPlanExpired;

  const handleCheckout = async (plan: Plan) => {
    const userId = localStorage.getItem("userId");
    if (currentPlanId && plan.id === currentPlanId && !isSubscriptionExpired) {
      return;
    }
    if (!userId) {
      navigate("/login");
      return;
    }

    setCheckoutError("");
    setCheckoutPlanId(plan.id);
    try {
      const numericPrice = Number(
        String(plan.price || "").replace(/[^0-9.]/g, ""),
      );
      const res = await api.post("/payment/checkout", {
        planId: plan.id,
        name: plan.planName,
        price: Number.isFinite(numericPrice) ? numericPrice : 0,
        userId,
        isMonthly: plan.cadence.toLowerCase().includes("month"),
      });

      const url = res?.data?.url;
      if (url) {
        window.location.href = url;
        return;
      }

      setCheckoutError("ไม่พบลิงก์เซสชัน Stripe กรุณาลองอีกครั้ง");
    } catch (err: any) {
      setCheckoutError(
        err?.response?.data?.message ||
          err?.message ||
          "ชำระเงินไม่สำเร็จ กรุณาลองอีกครั้ง",
      );
    } finally {
      setCheckoutPlanId(null);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#F7F3EF] text-[#1B1511]">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F7F3EF] via-[#F0EAE4] to-[#E8E0D8]" />
        <div className="relative mx-auto max-w-6xl px-4 pb-12 pt-10 text-center md:px-8">
          <div className="mb-6 flex justify-start">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/15 bg-white/70 text-[#1B1511] backdrop-blur-sm transition hover:border-black/30"
              aria-label="ย้อนกลับ"
              title="ย้อนกลับ"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          </div>
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/60 px-4 py-2 backdrop-blur-sm">
            <Sparkles className="h-4 w-4 text-[#1B1511]" />
            <span className="text-xs font-medium uppercase tracking-wider">
              เริ่มต้นการเดินทางของคุณ
            </span>
          </div>
          <h1 className="font-['Fraunces'] text-5xl font-semibold tracking-tight text-[#1B1511] md:text-7xl">
            เลือกแพ็กเกจของคุณ
          </h1>
          <p className="mx-auto mt-4 max-w-2xl font-['Manrope'] text-lg text-black/60">
            Every plan is designed to support your unique journey toward clarity
            and purpose. Upgrade, pause, or cancel anytime.
          </p>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="mx-auto w-full max-w-6xl flex-1 px-4 pb-20 md:px-8">
        {isLoading ? (
          <div className="mt-12 text-center text-sm text-black/60">
            กำลังโหลดแพ็กเกจ...
          </div>
        ) : activePlans.length === 0 ? (
          <div className="mt-12 text-center text-sm text-black/60">
            ขณะนี้ยังไม่มีแพ็กเกจที่เปิดให้บริการ
          </div>
        ) : (
          <div
            className={`mt-8 grid gap-6 ${
              activePlans.length === 1
                ? "grid-cols-1 justify-center justify-items-center max-w-[380px] mx-auto"
                : activePlans.length === 2
                  ? "grid-cols-1 justify-center justify-items-center sm:grid-cols-2 sm:gap-6 max-w-[780px] mx-auto"
                  : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
            }`}
          >
            {activePlans.map((plan) => {
              const isCurrentPlan = Boolean(
                currentPlanId && plan.id === currentPlanId,
              );
              const showExpired = isCurrentPlan && isSubscriptionExpired;

              return (
                <div
                  key={plan.id || plan.planName}
                  className={`group relative w-full max-w-[360px] justify-self-center overflow-hidden rounded-3xl border bg-white/90 p-6 shadow-[0_24px_60px_-45px_rgba(0,0,0,0.6)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_32px_72px_-45px_rgba(0,0,0,0.8)] ${
                    plan.popular
                      ? "border-[#1B1511] shadow-[0_24px_60px_-30px_rgba(27,21,17,0.3)]"
                      : "border-black/10 hover:border-black/20"
                  }`}
                >
                  {plan.popular ? (
                    <div className="absolute -right-8 top-10 rotate-45 bg-[#1B1511] px-8 py-1 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#F7F3EF]">
                      ยอดนิยม
                    </div>
                  ) : null}
                  <div className="text-xs uppercase tracking-[0.3em] text-black/50 font-['Manrope']">
                    {plan.planName}
                  </div>
                  <div className="mt-3 flex items-baseline gap-2">
                    <div className="text-4xl font-semibold text-[#1B1511] font-['Fraunces']">
                      ${plan.price}
                    </div>
                    <div className="text-sm text-black/60 font-['Manrope']">
                      {plan.cadence}
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-black/65 font-['Manrope']">
                    {plan.highlight}
                  </p>
                  <ul className="mt-5 space-y-3 text-sm text-black/80 font-['Manrope']">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <span className="mt-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#1B1511] text-[#F7F3EF]">
                          <Check className="h-3 w-3" />
                        </span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => handleCheckout(plan)}
                    disabled={
                      checkoutPlanId === plan.id ||
                      (isCurrentPlan && !showExpired)
                    }
                    className={`mt-6 w-full rounded-full px-4 py-3 text-sm font-semibold transition-all duration-300 ${
                      plan.popular
                        ? "bg-[#1B1511] text-[#F7F3EF] hover:scale-[1.02] hover:shadow-lg"
                        : "border border-black/20 text-[#1B1511] hover:border-black/50 hover:bg-black/5"
                    } ${
                      checkoutPlanId === plan.id ||
                      (isCurrentPlan && !showExpired)
                        ? "cursor-not-allowed opacity-70"
                        : ""
                    }`}
                  >
                    {checkoutPlanId === plan.id
                      ? "กำลังนำคุณไปยัง Stripe..."
                      : isCurrentPlan
                        ? showExpired
                          ? "แพ็กเกจหมดอายุ"
                          : "แพ็กเกจปัจจุบัน"
                        : `เลือก ${plan.planName}`}
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {checkoutError ? (
          <div className="mt-6 text-center text-sm text-red-600">
            {checkoutError}
          </div>
        ) : null}
      </div>

      <SiteFooter />
    </div>
  );
};

export default Subscription;
